import { useEffect, useState } from "react";

export const useActiveSection = (sectionIds: string[]) => {
  const [activeSection, setActiveSection] = useState<string>("");

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "-80px 0px -80% 0px", // 80px from top, 80% from bottom
      threshold: [0, 0.25, 0.5, 0.75, 1]
    };

    // Track all observed entries
    const observedEntries = new Map<string, IntersectionObserverEntry>();

    const getActiveSectionAtBottom = (
      intersectingSections: Array<{ id: string; top: number; ratio: number }>
    ): string | null => {
      const lastIntersectingId =
        intersectingSections[intersectingSections.length - 1].id;
      const lastSectionIndex = sectionIds.findIndex(
        (id) => id === lastIntersectingId
      );

      // Find the last visible section in document order
      let lastVisibleIndex = lastSectionIndex;
      for (let i = lastSectionIndex + 1; i < sectionIds.length; i++) {
        const entry = observedEntries.get(sectionIds[i]);
        if (entry && entry.isIntersecting) {
          lastVisibleIndex = i;
        }
      }

      return lastVisibleIndex !== -1 ? sectionIds[lastVisibleIndex] : null;
    };

    const getClosestSection = (
      intersectingSections: Array<{ id: string; top: number; ratio: number }>
    ): string | null => {
      const closestSection = intersectingSections.reduce((closest, current) => {
        // Prefer sections that are closer to the top
        if (current.top >= 0 && current.top < 100) {
          return current.top < closest.top ? current : closest;
        }
        // If no section is near the top, use the one with highest intersection ratio
        return current.ratio > closest.ratio ? current : closest;
      });

      const sectionIndex = sectionIds.findIndex(
        (id) => id === closestSection.id
      );

      return sectionIndex !== -1 ? sectionIds[sectionIndex] : null;
    };

    const checkScrollPosition = () => {
      // Check if user has scrolled to the bottom
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const clientHeight = window.innerHeight;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;

      // Get all currently intersecting sections
      const intersectingSections = Array.from(observedEntries.values())
        .filter((entry) => entry.isIntersecting)
        .map((entry) => ({
          id: entry.target.id,
          top: entry.boundingClientRect.top,
          ratio: entry.intersectionRatio
        }));

      if (intersectingSections.length === 0) return;

      // If at bottom, activate the last section in the ordered list
      if (isAtBottom) {
        const bottomSection = getActiveSectionAtBottom(intersectingSections);
        if (bottomSection) {
          setActiveSection(bottomSection);
          return;
        }
      }

      // Find the section closest to the top of the viewport
      const closestSection = getClosestSection(intersectingSections);
      if (closestSection) {
        setActiveSection(closestSection);
      }
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      // Update the map with latest entries
      entries.forEach((entry) => {
        observedEntries.set(entry.target.id, entry);
      });

      checkScrollPosition();
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    // Observe all section elements
    sectionIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    // Add scroll listener to detect when at bottom
    window.addEventListener("scroll", checkScrollPosition, { passive: true });

    return () => {
      observer.disconnect();
      observedEntries.clear();
      window.removeEventListener("scroll", checkScrollPosition);
    };
  }, [sectionIds]);

  return activeSection;
};
